import type {
  MentionDescription,
  MentionNameDetails,
  MentionResourceConfig,
  ResolvingMentionProvider,
} from '@atlaskit/mention/resource';
import {
  AbstractMentionResource,
  MentionNameStatus,
} from '@atlaskit/mention/resource';
import { clientVersion } from '@trello/config';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { trelloFetch } from '@trello/fetch';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { getCsrfRequestPayload } from '@trello/session-cookie/csrf';

import type { MentionProviderConfig } from './MentionProviderConfig';
import { sortMentions } from './sortMentions';
import { toMemberMention } from './toMemberMention';
import { toSpecialMention } from './toSpecialMention';
import type { TrelloMember } from './TrelloMember';

export interface ResourcedMentionProviderConfig
  extends MentionProviderConfig,
    MentionResourceConfig {}

const TOTAL_COUNT = 20;

interface RequestBody {
  cardId: string | null;
  orgId: string | null;
  query?: string;
  usernames?: string[];
  /**
   * The default value for the API is 25; and max is 100. However, the user
   * picker in Editor only renders the first 20 results. So just cap it at 20.
   */
  totalCount?: typeof TOTAL_COUNT;
}

type CacheKey = `${string}:${string}`;

const resolvedMentionCache: Map<CacheKey, MentionNameDetails> = new Map();

export class ResourcedMentionProvider
  extends AbstractMentionResource
  implements ResolvingMentionProvider
{
  private config: ResourcedMentionProviderConfig;
  public resolvedMentionCache: Map<CacheKey, MentionNameDetails>;
  static productName: 'trello';
  static shouldEnableInvite: false;
  private debouncedFilter: ReturnType<typeof setTimeout> | null;
  private debounceInterval: number;

  constructor(config: ResourcedMentionProviderConfig) {
    super();
    this.config = config;
    this.resolvedMentionCache = resolvedMentionCache;
    this.debouncedFilter = null;
    this.debounceInterval = 200;
  }

  async fetch(query: string | null, username: string | null) {
    const { cardId, workspaceId } = this.config;

    const body: RequestBody = {
      cardId,
      orgId: workspaceId,
    };

    if (username) {
      body.usernames = [username];
    } else {
      body.query = query ?? '';
      body.totalCount = TOTAL_COUNT;
    }

    try {
      const response = await trelloFetch(
        this.config.url,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ ...body, ...getCsrfRequestPayload() }),
        },
        {
          clientVersion,
        },
      );

      if (!response.ok) {
        sendNetworkErrorEvent({
          url: this.config.url,
          response: await response.clone().text(),
          status: response.status,
        });
        return { mentions: [] };
      }

      return response.json();
    } catch (e) {
      // do nothing for now
      return { mentions: [] };
    }
  }
  public notify(mentions: MentionDescription[], query: string): void {
    this._notifyListeners({ mentions, query }, {});
    this._notifyAllResultsListeners({ mentions, query });
  }

  private async _filter(query: string = ''): Promise<void> {
    const { mentions: rawMentions } = await this.fetch(query, null);

    // Map TrelloMember objects to MentionDescription objects
    const mentions = rawMentions.map((member: TrelloMember) => {
      return member.type === 'special'
        ? toSpecialMention(member)
        : toMemberMention(member);
    });

    this.notify(mentions, query);
    return;
  }

  public async filter(query: string = ''): Promise<void> {
    if (this.debouncedFilter) {
      clearTimeout(this.debouncedFilter);
    }

    // If the user has entered text to search for a mention, issue the filter
    // request but debounce to avoid unnecessary requests to the API.
    if (query) {
      this.debouncedFilter = setTimeout(
        () => this._filter(query),
        this.debounceInterval,
      );
      return Promise.resolve();
    }

    // If the query is empty, just present the user with the list of current
    // board members and applicable special mentions. This is meant to reduce
    // lag between the user typing "@" and the list being rendered.

    const { currentMember, memberList, specialMentions } = this.config;

    // Filter out the currentMember because we're going to put them first in
    // the list. Then sort all board members by name. Once sorted, append any
    // special mentions to the end of the list. This more or less mimics the
    // behavior of the API.
    // Also, limit the list to 20 results including all special mentions.

    const sortedMembers = memberList
      .filter((member) => member.id !== currentMember?.id)
      .sort(sortMentions);

    if (currentMember) {
      sortedMembers.unshift(currentMember);
    }

    const end = TOTAL_COUNT - specialMentions.length;

    const mentions = sortedMembers
      .slice(0, end)
      .concat(specialMentions)
      .map((member: TrelloMember) => {
        return member.type === 'special'
          ? toSpecialMention(member)
          : toMemberMention(member);
      });

    this.notify(mentions, query);
    return Promise.resolve();
  }

  recordMentionSelection(mention: MentionDescription): void {
    this.config.onMentionSelect?.(mention);
  }

  // When rendering a mention within Editor, we have the user id for the user
  // being mentioned, but when rendering a mention in Renderer, we only have
  // the username. So we need to match on either.
  shouldHighlightMention({ id: idOrUsername }: MentionDescription): boolean {
    const isSpecialMention = this.config.specialMentions
      .map(toSpecialMention)
      .some(
        (m) =>
          idOrUsername === m.id ||
          idOrUsername === m.mentionName ||
          m.id.startsWith(idOrUsername),
      );

    const memberId = this.config.currentMember?.id;
    const username = dangerouslyConvertPrivacyString(
      this.config.currentMember?.username,
    );

    const isCurrentMember =
      idOrUsername === memberId || idOrUsername === username;

    return isCurrentMember || isSpecialMention;
  }

  getAllValidMentions() {
    const userMentions = this.config.memberList.map(toMemberMention);
    const specialMentions = this.config.specialMentions.map(toSpecialMention);
    return userMentions.concat(specialMentions);
  }

  async resolveMentionName(id: string): Promise<MentionNameDetails> {
    const { workspaceId } = this.config;
    const cacheKey: CacheKey = `${workspaceId}:${id}`;

    const cachedMention = this.resolvedMentionCache.get(cacheKey);

    if (cachedMention) {
      return Promise.resolve(cachedMention);
    }

    const { mentions } = await this.fetch(null, id);
    const member = mentions?.length ? mentions[0] : null;

    if (member) {
      this.cacheMentionName(member.id, member.username);
    } else {
      this.cacheMentionName('', id);
    }

    const unknownMention = {
      id,
      name: id,
      status: MentionNameStatus.UNKNOWN,
    };

    const mention = this.resolvedMentionCache.get(cacheKey);

    return mention ?? unknownMention;
  }

  cacheMentionName(id: string, name: string) {
    if (!name) {
      return;
    }

    const { workspaceId } = this.config;
    const cacheKey: CacheKey = `${workspaceId}:${name}`;

    const mention = {
      id,
      name,
      status: id ? MentionNameStatus.OK : MentionNameStatus.UNKNOWN,
    };

    this.resolvedMentionCache.set(cacheKey, mention);
  }

  supportsMentionNameResolving() {
    return false;
  }
}
